/**
 * Mẫu lời chào mừng cho Dashboard - 5 kiểu khác nhau mỗi vai trò để tránh nhàm chán.
 * Luân phiên theo ngày (dayOfYear % 5) để mỗi ngày hiển thị một kiểu.
 * Admin có thể cấu hình qua setting dashboard_greetings (JSON).
 */

import { UI_CONFIG } from '../config';

type Role = 'teacher' | 'parent' | 'student' | 'child';

const TEACHER_GREETINGS: ((name: string) => string)[] = [
  (n) => `Chào mừng trở lại, ${n}!`,
  (n) => `Xin chào ${n}, sẵn sàng tạo bài giảng tuyệt vời chưa?`,
  (n) => `Chúc ${n} một ngày giảng dạy hiệu quả!`,
  (n) => `Rất vui được gặp lại ${n}! Hôm nay chúng ta tạo gì nhé?`,
  (n) => `${n} ơi, AI đã sẵn sàng hỗ trợ bạn rồi đấy!`,
];

const PARENT_GREETINGS: ((name: string) => string)[] = [
  (n) => `Xin chào, Phụ huynh ${n}!`,
  (n) => `Chào ${n}! Cùng theo dõi tiến độ học tập của con nhé.`,
  (n) => `Rất vui gặp lại ${n}! Con bạn đang tiến bộ từng ngày.`,
  (n) => `Chào mừng ${n} trở lại! Đây là cập nhật mới nhất từ nhà trường.`,
  (n) => `${n} ơi, hãy xem con đã học được những gì hôm nay!`,
];

const STUDENT_GREETINGS: ((name: string) => string)[] = [
  (n) => `Chào mừng trở lại, ${n}!`,
  (n) => `Xin chào ${n}! Hôm nay bạn muốn học gì nào?`,
  (n) => `Chúc ${n} một ngày học tập hiệu quả!`,
  (n) => `Rất vui gặp lại ${n}! Cùng AI chinh phục mục tiêu mới nhé!`,
  (n) => `${n} ơi, sẵn sàng khám phá bài học mới chưa?`,
];

const PARENT_SUBTITLES: ((n: number) => string)[] = [
  (n) => n > 0 ? `Hệ thống AI đang theo dõi tiến độ của ${n} con. Dưới đây là tổng hợp các cập nhật mới nhất từ nhà trường và trung tâm.` : 'Dưới đây là tổng hợp các cập nhật mới nhất từ nhà trường và trung tâm.',
  (n) => n > 0 ? `Cập nhật nhanh về ${n} con: bài tập, điểm số và nhận xét từ giáo viên. Mọi thứ đều ở đây!` : 'Cập nhật nhanh về bài tập, điểm số và nhận xét từ giáo viên. Mọi thứ đều ở đây!',
  (n) => n > 0 ? `Theo dõi lộ trình học tập của ${n} con mỗi ngày. Hãy cùng đồng hành với con trên con đường chinh phục tri thức.` : 'Theo dõi lộ trình học tập của con mỗi ngày. Hãy cùng đồng hành với con trên con đường chinh phục tri thức.',
  (n) => n > 0 ? `Tin tức mới nhất từ lớp học của ${n} con. Luôn cập nhật để không bỏ lỡ bất kỳ thông tin nào.` : 'Tin tức mới nhất từ lớp học và trung tâm. Luôn cập nhật để không bỏ lỡ bất kỳ thông tin nào.',
  (n) => n > 0 ? `Bảng tổng hợp tiến độ của ${n} con. Một cái nhìn toàn diện về hành trình học tập.` : 'Bảng tổng hợp tiến độ học tập. Một cái nhìn toàn diện về hành trình của con bạn.',
];

const STUDENT_SUBTITLES: string[] = [
  'Hôm nay bạn muốn học gì nào? Hãy cùng AI chinh phục các mục tiêu mới nhé!',
  'Sẵn sàng cho một ngày học tập hiệu quả? Các bài tập và lộ trình đang chờ bạn!',
  'Khám phá bài học mới, hoàn thành nhiệm vụ và theo dõi tiến độ của bạn. Bắt đầu ngay!',
  'Lộ trình cá nhân hóa đã sẵn sàng. Hãy chọn một mục tiêu và bắt đầu hành trình!',
  'AI luôn bên cạnh hỗ trợ bạn. Hãy tận dụng mọi công cụ để học tốt hơn mỗi ngày!',
];

const CHILD_HEADINGS: ((name: string) => string)[] = [
  (n) => `Bảng điều khiển của ${n}`,
  (n) => `Chào con ${n}! Đây là không gian học tập của con.`,
  (n) => `Chúc ${n} một ngày học vui!`,
  (n) => `Xin chào ${n}! Các bài tập đang chờ con đấy.`,
  (n) => `${n} ơi, cùng khám phá bài học mới nhé!`,
];

const TEACHER_SUBTITLES: string[] = [
  'Bảng điều khiển của bạn. Bắt đầu tạo tài liệu giảng dạy tuyệt vời ngay hôm nay.',
  'Công cụ AI sẵn sàng. Tạo giáo án, bài kiểm tra, trò chơi và nhiều hơn thế chỉ trong vài phút.',
  'Một ngày giảng dạy hiệu quả bắt đầu từ đây. Chọn công cụ và bắt đầu sáng tạo!',
  'Tất cả công cụ bạn cần đều ở đây. Từ soạn giáo án đến quản lý lớp học.',
  'Sáng tạo không giới hạn. Hãy để AI giúp bạn tiết kiệm thời gian và nâng cao chất lượng bài giảng.',
];

/** Lấy chỉ số luân phiên 0-4 dựa trên ngày (ổn định trong ngày, đổi mỗi ngày) */
function getDailyIndex(): number {
  const now = new Date();
  const dayOfYear = Math.floor((now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / (24 * 60 * 60 * 1000));
  return dayOfYear % 5;
}

function getTemplate(role: Role, idx: number, name: string): string {
  const custom = UI_CONFIG.DASHBOARD_GREETINGS?.[role];
  if (Array.isArray(custom) && custom[idx]) {
    return String(custom[idx]).replace(/\{\{name\}\}/g, name);
  }
  const fallbacks: Record<Role, ((n: string) => string)[]> = {
    teacher: TEACHER_GREETINGS,
    parent: PARENT_GREETINGS,
    student: STUDENT_GREETINGS,
    child: CHILD_HEADINGS,
  };
  const arr = fallbacks[role];
  return arr && arr[idx] ? arr[idx](name) : `Chào mừng trở lại, ${name}!`;
}

/**
 * Lấy lời chào mừng cho Dashboard theo vai trò.
 * Nếu user đã có greeting tùy chỉnh (từ backend/admin) thì ưu tiên dùng.
 */
export function getDashboardGreeting(
  role: Role,
  displayName: string,
  customGreeting?: string | null
): string {
  if (customGreeting?.trim()) return customGreeting;
  const idx = getDailyIndex();
  const name = displayName || 'Bạn';
  return getTemplate(role, idx, name);
}


/** Lấy phụ đề (subtitle) cho section chào mừng - luân phiên theo ngày. Parent có thể truyền childrenCount. */
export function getDashboardSubtitle(role: Role, options?: { childrenCount?: number }): string {
  const idx = getDailyIndex();
  const n = options?.childrenCount ?? 0;
  switch (role) {
    case 'teacher':
      return TEACHER_SUBTITLES[idx];
    case 'parent':
      return PARENT_SUBTITLES[idx](n);
    case 'student':
      return STUDENT_SUBTITLES[idx];
    default:
      return '';
  }
}
